import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  DateRangePickerProps,
  TableProps,
} from '@cloudscape-design/components';

import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUrlFilterState, useUrlRangeState } from '../../common/filtering/filters';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { PropertyDateFilter } from '../../common/filtering/date-filtering';
import { buildFiltering } from '../../common/filtering/lists';
import { ResourceError } from '../Loading';
import { exportExcel } from '../../common/excel';
import { PropertyFilterOption, PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { Place, PlaceContact, PlaceList } from '../../common/types';
import { getLists, getListMeta } from '../../common/api/lists';
import { CreateListModal } from '../CreateListModal';
import { formatDateString } from '../../common/formatting/dateFormat';

export type ListTableProps = {
  now: Date;
  setNow: (now: Date) => void;
  query: PropertyFilterQuery;
  setQuery?: (query: PropertyFilterQuery) => void;
  range?: DateRangePickerProps.Value | null;
  setRange?: (range: DateRangePickerProps.Value | null) => void;
  hideActions?: boolean;
  hideFilter?: boolean;
  hidePagination?: boolean;
  hideColumns?: string[];
  variant?: 'embedded' | 'borderless' | 'stacked' | 'full-page';
  additionalActions?: React.ReactNode;
  selectedItems?: PlaceList[];
  setSelectedItems?: (items: PlaceList[]) => void;
};

export function ListTable(props: ListTableProps) {
  const { now, setNow, query, setQuery, range, setRange, selectedItems, setSelectedItems } = props;
  const navigate = useNavigate();
  const resourceName = 'Lists';
  const endpointName = 'lists';
  // relative times are in the past vs future
  const past = true;

  // TODO move to context
  const DEFAULT_PREFERENCES = {
    pageSize: 50,
  };
  const PAGE_SIZE_OPTIONS = [
    { value: 10, label: `10 ${resourceName}` },
    { value: 20, label: `20 ${resourceName}` },
    { value: 50, label: `50 ${resourceName}` },
    { value: 100, label: `100 ${resourceName}` },
  ];

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps.Preferences<unknown>
  >('React-Cards-Preferences', DEFAULT_PREFERENCES);

  const pageSize = preferences?.pageSize || DEFAULT_PREFERENCES.pageSize;

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  // const queryClient = useQueryClient();
  const metaQuery = useQuery({
    queryKey: [endpointName, 'meta'],
    queryFn: getListMeta,
    staleTime: 360_000, // 10 minutes
  });

  const { options, properties } = useMemo(() => {
    if (metaQuery.data) {
      return {
        options: metaQuery.data.filteringOptions as PropertyFilterOption[],
        properties: metaQuery.data.filteringProperties,
      };
    }
    return {
      options: [],
      properties: [],
    };
  }, [metaQuery.data]);

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data } = useQuery({
    queryKey: [endpointName, query, range, pageSize, page, now],
    enabled: metaQuery.isSuccess,
    // TODO add sorting
    queryFn: async () => await getLists(query, page, pageSize, range, now),
    // 1 minute
    staleTime: 60_000,
    // placeholderData: keepPreviousData,
  });

  const loading =
    (isPending || isFetching || metaQuery.isPending || metaQuery.isFetching) &&
    !error &&
    !metaQuery.error;

  useEffect(() => {
    if (data && data.pages !== pageCount) {
      setPageCount(data.pages);
    }
  }, [data]);

  const refresh = () => {
    setNow(new Date());
    metaQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={pageCount}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  return (
    <Table
      header={
        <Header
          variant={
            !props.variant
              ? 'awsui-h1-sticky'
              : props.variant !== 'full-page'
                ? 'h2'
                : 'awsui-h1-sticky'
          }
          description="View and manage lists for places."
          actions={
            props.hideActions ? undefined : (
              <SpaceBetween size="xs" direction="horizontal">
                {props.additionalActions}
                <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
              </SpaceBetween>
            )
          }
          counter={
            data?.total && metaQuery.data?.total
              ? `(${data.total.toLocaleString()}/${metaQuery.data.total.toLocaleString()})`
              : undefined
          }
        >
          {resourceName}
        </Header>
      }
      items={data?.items ?? []}
      selectedItems={selectedItems}
      onSelectionChange={
        setSelectedItems ? ({ detail }) => setSelectedItems(detail.selectedItems) : undefined
      }
      selectionType={setSelectedItems ? 'multi' : undefined}
      loading={loading}
      loadingText={`Loading ${resourceName}...`}
      pagination={props.hidePagination ? undefined : pagination}
      preferences={
        <CollectionPreferences
          disabled={loading}
          pageSizePreference={{
            options: PAGE_SIZE_OPTIONS,
          }}
          preferences={preferences}
          onConfirm={(e) => setPreferences(e.detail)}
        />
      }
      filter={
        props.hideFilter ? undefined : (
          <PropertyDateFilter
            loading={loading}
            query={query}
            setQuery={(q) => {
              setQuery?.(q);
              // reset page when filtering
              setPage(1);
            }}
            range={range ?? null}
            setRange={(r) => {
              setRange?.(r);
              // reset page when filtering
              setPage(1);
            }}
            propertyFilteringOptions={options}
            filteringProperties={properties}
            past={past}
          />
        )
      }
      variant={props.variant ?? 'full-page'}
      stickyHeader={!props.variant || props.variant === 'full-page'}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {error || metaQuery.error ? (
            <ResourceError
              error={error ?? metaQuery.error ?? new Error('Unknown error')}
              refetch={refresh}
              resourceName={resourceName}
            />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No {resourceName.toLowerCase()}</b>
                <Box color="inherit" variant="p">
                  No {resourceName.toLowerCase()} found.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          cell: (item: PlaceList) => (
            <Link
              href={`/dashboard/lists/${item.uuid}`}
              variant="secondary"
              onFollow={(e) => {
                e.preventDefault();
                navigate({ to: `/dashboard/lists/${item.uuid}` });
              }}
            >
              {item.name}
            </Link>
          ),
        },
        {
          id: 'description',
          header: 'Description',
          cell: (item: PlaceList) => <Box variant="p">{item.description}</Box>,
        },
        {
          id: 'size',
          header: 'Size',
          cell: (item: PlaceList) => <Box variant="p">{item.places.length.toLocaleString()}</Box>,
        },
        {
          id: 'created',
          header: 'Created',
          cell: (item: PlaceList) => formatDateString(item.created_at),
        },
      ].filter((column) => !props.hideColumns?.includes(column.id))}
    />
  );
}
